/**
 * Georgian ↔ Latin in three systems: the Georgian national system (2002), ISO 9984:1996 and
 * ALA-LC (Library of Congress).
 *
 * Sources:
 *
 * - National system — adopted in February 2002 by the State Department of Geodesy and
 *   Cartography of Georgia and the Institute of Linguistics of the Georgian National Academy of
 *   Sciences; approved by the UN Group of Experts on Geographical Names; used by BGN/PCGN since
 *   2009. Read from the UNGEGN "Technical reference manual for the standardization of
 *   geographical names" (2007) via the comparison tables of the Estonian Institute of the
 *   Language (T. T. Pedersen, "Georgian", rev. 3.0, 2007) and the Wikipedia article
 *   "Romanization of Georgian". Ejective consonants (კ პ ტ ყ წ ჭ) take an apostrophe; the
 *   aspirated ones (თ ფ ქ ც ჩ) do not; digraphs sh, zh, gh, kh, dz, ch, ts are used.
 * - ISO 9984:1996 — one Latin letter per Georgian letter, no digraphs and no capitals;
 *   aspirates carry U+0315 (combining comma above right) and ejectives are unmarked (კ k,
 *   ქ k̕); ღ is ḡ, ჯ is ǰ. The standard is paid; the values come from the same two comparison
 *   tables.
 * - ALA-LC — the Library of Congress "Georgian Romanization Table", 2011 version
 *   (loc.gov/catdir/cpso/romanization/georgian.pdf): all letters, including the Asomtavruli /
 *   Nuskhuri ("Khutsuri") capitals, which are romanized as capital letters. The aspirate sign is
 *   ʻ (U+02BB).
 *
 * What a program cannot do: the national system and ALA-LC both use digraphs, so reading them
 * back is a best guess where a pair such as s+h could be შ (sh) or ს+ჰ. Letters that only
 * appear in archaic or dialect writing (ჱ ჲ ჳ ჴ ჵ ჶ ჷ ჸ) have no value in the national system
 * and are left unchanged there.
 */

export type GeorgianSystem = "national" | "iso" | "ala-lc";

export interface GeorgianOptions {
  /** Capitalize the first letter of each word (not in ISO 9984, which has no capitals). */
  capitalize: boolean;
}

export interface GeorgianResult {
  text: string;
  /** Characters left unchanged because the system has no value for them. */
  unknown: string[];
}

const COMBINING_COMMA = "̕";
const LC_ASPIRATE = "ʻ";

/** Mkhedruli letter, national system, ISO 9984, ALA-LC. */
const LETTERS: readonly (readonly [string, string, string, string])[] = [
  ["ა", "a", "a", "a"],
  ["ბ", "b", "b", "b"],
  ["გ", "g", "g", "g"],
  ["დ", "d", "d", "d"],
  ["ე", "e", "e", "e"],
  ["ვ", "v", "v", "v"],
  ["ზ", "z", "z", "z"],
  ["თ", "t", `t${COMBINING_COMMA}`, `t${LC_ASPIRATE}`],
  ["ი", "i", "i", "i"],
  ["კ", "k'", "k", "k"],
  ["ლ", "l", "l", "l"],
  ["მ", "m", "m", "m"],
  ["ნ", "n", "n", "n"],
  ["ო", "o", "o", "o"],
  ["პ", "p'", "p", "p"],
  ["ჟ", "zh", "ž", "ž"],
  ["რ", "r", "r", "r"],
  ["ს", "s", "s", "s"],
  ["ტ", "t'", "t", "t"],
  ["უ", "u", "u", "u"],
  ["ფ", "p", `p${COMBINING_COMMA}`, `p${LC_ASPIRATE}`],
  ["ქ", "k", `k${COMBINING_COMMA}`, `k${LC_ASPIRATE}`],
  ["ღ", "gh", "ḡ", "ġ"],
  ["ყ", "q'", "q", "q"],
  ["შ", "sh", "š", "š"],
  ["ჩ", "ch", `č${COMBINING_COMMA}`, `č${LC_ASPIRATE}`],
  ["ც", "ts", `c${COMBINING_COMMA}`, `c${LC_ASPIRATE}`],
  ["ძ", "dz", "j", "ż"],
  ["წ", "ts'", "c", "c"],
  ["ჭ", "ch'", "č", "č"],
  ["ხ", "kh", "x", "x"],
  ["ჯ", "j", "ǰ", "j"],
  ["ჰ", "h", "h", "h"],
];

/** Archaic and dialect letters, with no value in the national system (empty string). */
const EXTRA_LETTERS: readonly (readonly [string, string, string, string])[] = [
  ["ჱ", "", "ē", "ē"],
  ["ჲ", "", "y", "y"],
  ["ჳ", "", "w", "w"],
  ["ჴ", "", "ẖ", "x̣"],
  ["ჵ", "", "ō", "ō"],
  ["ჶ", "", "f", "f"],
  ["ჷ", "", "", "ĕ"],
  ["ჸ", "", "", LC_ASPIRATE],
];

const ASOMTAVRULI_START = 0x10_a0;
const ASOMTAVRULI_END = 0x10_c5;
const NUSKHURI_START = 0x2d_00;
const NUSKHURI_END = 0x2d_25;
const MTAVRULI_START = 0x1c_90;
const MTAVRULI_END = 0x1c_ba;
const MKHEDRULI_START = 0x10_d0;

const COLUMN: Record<GeorgianSystem, 1 | 2 | 3> = {
  national: 1,
  iso: 2,
  "ala-lc": 3,
};

const FORWARD: Record<GeorgianSystem, Map<string, string>> = {
  national: new Map(),
  iso: new Map(),
  "ala-lc": new Map(),
};
for (const row of [...LETTERS, ...EXTRA_LETTERS]) {
  for (const system of ["national", "iso", "ala-lc"] as const) {
    const value = row[COLUMN[system]] as string;
    if (value) {
      FORWARD[system].set(row[0], value.normalize("NFC"));
    }
  }
}

interface Normalized {
  mkhedruli: string;
  /** True for the capital forms (Asomtavruli, Mtavruli), which LC romanizes as capitals. */
  capital: boolean;
}

function normalizeLetter(ch: string): Normalized {
  const code = ch.codePointAt(0) ?? 0;
  if (code >= ASOMTAVRULI_START && code <= ASOMTAVRULI_END) {
    return {
      mkhedruli: String.fromCodePoint(
        code - ASOMTAVRULI_START + MKHEDRULI_START
      ),
      capital: true,
    };
  }
  if (code >= NUSKHURI_START && code <= NUSKHURI_END) {
    return {
      mkhedruli: String.fromCodePoint(code - NUSKHURI_START + MKHEDRULI_START),
      capital: false,
    };
  }
  if (code >= MTAVRULI_START && code <= MTAVRULI_END) {
    return {
      mkhedruli: String.fromCodePoint(code - MTAVRULI_START + MKHEDRULI_START),
      capital: true,
    };
  }
  return { mkhedruli: ch, capital: false };
}

function isGeorgian(ch: string | undefined): boolean {
  if (ch === undefined) {
    return false;
  }
  const code = ch.codePointAt(0) ?? 0;
  return (
    (code >= ASOMTAVRULI_START && code <= ASOMTAVRULI_END) ||
    (code >= MKHEDRULI_START && code <= 0x10_fa) ||
    (code >= NUSKHURI_START && code <= NUSKHURI_END) ||
    (code >= MTAVRULI_START && code <= MTAVRULI_END)
  );
}

function capitalizeFirst(text: string): string {
  const first = [...text][0] ?? "";
  return first.toUpperCase() + text.slice(first.length);
}

/** Romanize Georgian-script text (Mkhedruli, Nuskhuri, Asomtavruli, Mtavruli); other characters pass through. */
export function romanizeGeorgian(
  input: string,
  system: GeorgianSystem,
  options: GeorgianOptions
): GeorgianResult {
  const table = FORWARD[system];
  const chars = [...input.normalize("NFC")];
  const unknown = new Set<string>();
  let out = "";
  let wordStart = true;
  for (const ch of chars) {
    if (!isGeorgian(ch)) {
      out += ch;
      wordStart = !WORD_CHAR.test(ch);
      continue;
    }
    const { mkhedruli, capital } = normalizeLetter(ch);
    const value = table.get(mkhedruli);
    if (value === undefined) {
      unknown.add(ch);
      out += ch;
    } else {
      const upper =
        system !== "iso" && (capital || (options.capitalize && wordStart));
      out += upper ? capitalizeFirst(value) : value;
    }
    wordStart = false;
  }
  return { text: out.normalize("NFC"), unknown: [...unknown] };
}

// ---------------------------------------------------------------------------------------
// Latin → Georgian
// ---------------------------------------------------------------------------------------

const MAX_KEY = 4;
const LETTER = /\p{L}/u;
const WORD_CHAR = /[\p{L}\p{N}]/u;
const NATIONAL_MARKS = /[’ʼʻ‘`]/g;
const ISO_MARKS = /([tpkcč])[ʼ’ʻ'‘]/gi;
const LC_MARKS = /[ʼ’‘`']/g;

function buildReverse(system: GeorgianSystem): Map<string, string> {
  const table = new Map<string, string>();
  for (const [georgian, value] of FORWARD[system]) {
    table.set(value.normalize("NFC"), georgian);
  }
  return table;
}

const REVERSE: Record<GeorgianSystem, Map<string, string>> = {
  national: buildReverse("national"),
  iso: buildReverse("iso"),
  "ala-lc": buildReverse("ala-lc"),
};

function normalizeMarks(text: string, system: GeorgianSystem): string {
  if (system === "national") {
    return text.replace(NATIONAL_MARKS, "'");
  }
  if (system === "iso") {
    return text.replace(ISO_MARKS, `$1${COMBINING_COMMA}`);
  }
  return text.replace(LC_MARKS, LC_ASPIRATE);
}

function matchLatin(
  chars: readonly string[],
  at: number,
  table: ReadonlyMap<string, string>
): { georgian: string; length: number } | undefined {
  for (
    let length = Math.min(MAX_KEY, chars.length - at);
    length > 0;
    length -= 1
  ) {
    const key = chars
      .slice(at, at + length)
      .join("")
      .toLowerCase();
    const georgian = table.get(key);
    if (georgian) {
      return { georgian, length };
    }
  }
  return;
}

/** True when a hyphen between two Latin letters is there to keep them from forming a digraph. */
function isSeparator(
  chars: readonly string[],
  i: number,
  previous: string,
  table: ReadonlyMap<string, string>
): boolean {
  const next = chars[i + 1];
  if (chars[i] !== "-" || next === undefined || !previous) {
    return false;
  }
  return table.has((previous.at(-1) + next).toLowerCase());
}

/** Convert national-system, ISO 9984 or ALA-LC Latin text to Georgian (Mkhedruli). */
export function latinToGeorgian(
  input: string,
  system: GeorgianSystem
): GeorgianResult {
  const table = REVERSE[system];
  const chars = [
    ...normalizeMarks(input.normalize("NFC"), system).normalize("NFC"),
  ];
  const unknown = new Set<string>();
  let out = "";
  let previous = "";
  let i = 0;
  while (i < chars.length) {
    if (isSeparator(chars, i, previous, table)) {
      i += 1;
      previous = "";
      continue;
    }
    const hit = matchLatin(chars, i, table);
    if (hit) {
      out += hit.georgian;
      previous = chars.slice(i, i + hit.length).join("");
      i += hit.length;
      continue;
    }
    const ch = chars[i] as string;
    if (LETTER.test(ch)) {
      unknown.add(ch);
    }
    out += ch;
    previous = "";
    i += 1;
  }
  return { text: out.normalize("NFC"), unknown: [...unknown] };
}

// ---------------------------------------------------------------------------------------
// Letter table
// ---------------------------------------------------------------------------------------

export interface GeorgianLetterRow {
  georgian: string;
  national: string;
  iso: string;
  alaLc: string;
  archaic: boolean;
}

/** The letter table: every letter with its value in each system. */
export function georgianLetterRows(): GeorgianLetterRow[] {
  const toRow = (
    row: readonly [string, string, string, string],
    archaic: boolean
  ): GeorgianLetterRow => ({
    georgian: row[0],
    national: row[1],
    iso: row[2].normalize("NFC"),
    alaLc: row[3].normalize("NFC"),
    archaic,
  });
  return [
    ...LETTERS.map((row) => toRow(row, false)),
    ...EXTRA_LETTERS.map((row) => toRow(row, true)),
  ];
}

export const GEORGIAN_SYSTEMS: readonly GeorgianSystem[] = [
  "national",
  "iso",
  "ala-lc",
];
